import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";
import { buildRedirectUrls, PC_STATE } from "./constants";

const ACCOUNT_URL = `${process.env.REACT_APP_API_URL}/payments/collect/account/`;
const ONBOARDING_LINKS_URL = `${ACCOUNT_URL}onboarding-links/`;
const LOGIN_LINKS_URL = `${ACCOUNT_URL}login-links/`;

const deriveState = (account) => {
  if (!account) return PC_STATE.NOT_CONNECTED;
  if (account.status === "pending") {
    return account.details_submitted ? PC_STATE.PENDING : PC_STATE.INCOMPLETE;
  }
  return account.status;
};

export const fetchConnectAccount = createAsyncThunk(
  "paymentConnection/fetch",
  async (_, { rejectWithValue }) => {
    try {
      return await request(ACCOUNT_URL, { method: "GET" });
    } catch (error) {
      if (error?.status === 404) {
        return rejectWithValue({ notConnected: true });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Failed to load payment status",
      });
    }
  }
);

export const createConnectAccount = createAsyncThunk(
  "paymentConnection/create",
  async (_, { rejectWithValue }) => {
    try {
      return await request(ACCOUNT_URL, {
        method: "POST",
        body: JSON.stringify(buildRedirectUrls()),
      });
    } catch (error) {
      if (error?.status === 403) {
        return rejectWithValue({ subscriptionRequired: true });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't start setup",
        code: error?.error,
      });
    }
  }
);

export const createOnboardingLink = createAsyncThunk(
  "paymentConnection/onboardingLink",
  async (_, { rejectWithValue }) => {
    try {
      return await request(ONBOARDING_LINKS_URL, {
        method: "POST",
        body: JSON.stringify(buildRedirectUrls()),
      });
    } catch (error) {
      if (error?.status === 403) {
        return rejectWithValue({ subscriptionRequired: true });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't resume setup",
        code: error?.error,
      });
    }
  }
);

export const createLoginLink = createAsyncThunk(
  "paymentConnection/loginLink",
  async (_, { rejectWithValue }) => {
    try {
      // Security: this url is single-use — never persist or log it.
      const data = await request(LOGIN_LINKS_URL, { method: "POST" });
      return data?.url;
    } catch (error) {
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't open Stripe dashboard",
        code: error?.error,
      });
    }
  }
);

export const setAccountDisabled = createAsyncThunk(
  "paymentConnection/setDisabled",
  async (disabled, { rejectWithValue }) => {
    try {
      return await request(ACCOUNT_URL, {
        method: "PATCH",
        body: JSON.stringify({ disabled }),
      });
    } catch (error) {
      return rejectWithValue({
        message: error?.user_error_msg || "Update failed",
        code: error?.error,
      });
    }
  }
);

export const removeConnectAccount = createAsyncThunk(
  "paymentConnection/remove",
  async (_, { rejectWithValue }) => {
    try {
      await request(ACCOUNT_URL, { method: "DELETE" });
      return true;
    } catch (error) {
      if (
        error?.status === 409 &&
        error?.error === "payment_collect.balance_pending"
      ) {
        return rejectWithValue({ balancePending: true, detail: error.detail });
      }
      return rejectWithValue({
        message: error?.user_error_msg || "Couldn't remove setup",
        code: error?.error,
      });
    }
  }
);

const initialState = {
  account: null,
  derivedState: PC_STATE.LOADING,
  fetchStatus: "idle",
  actionStatus: "idle",
  actionError: null,
  cantRemoveDetail: null,
};

const paymentConnectionSlice = createSlice({
  name: "paymentConnection",
  initialState,
  reducers: {
    clearActionStatus: (state) => {
      state.actionStatus = "idle";
      state.actionError = null;
    },
    clearCantRemove: (state) => {
      state.cantRemoveDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectAccount.pending, (state) => {
        state.fetchStatus = "loading";
      })
      .addCase(fetchConnectAccount.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        state.account = action.payload;
        state.derivedState = deriveState(action.payload);
      })
      .addCase(fetchConnectAccount.rejected, (state, action) => {
        state.fetchStatus = "failed";
        if (action.payload?.notConnected) {
          state.account = null;
          state.derivedState = PC_STATE.NOT_CONNECTED;
        } else {
          state.derivedState = PC_STATE.ERROR_FETCH;
          state.actionError = action.payload;
        }
      });

    [
      createConnectAccount,
      createOnboardingLink,
      createLoginLink,
      setAccountDisabled,
      removeConnectAccount,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.actionStatus = "loading";
          state.actionError = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.actionStatus = "failed";
          state.actionError = action.payload;
          if (action.payload?.balancePending) {
            state.cantRemoveDetail = action.payload.detail || {};
          }
        });
    });

    builder
      .addCase(setAccountDisabled.fulfilled, (state, action) => {
        state.actionStatus = "success";
        state.account = action.payload;
        state.derivedState = deriveState(action.payload);
      })
      .addCase(removeConnectAccount.fulfilled, (state) => {
        state.actionStatus = "success";
        state.account = null;
        state.derivedState = PC_STATE.NOT_CONNECTED;
      })
      .addCase(createConnectAccount.fulfilled, (state) => {
        state.actionStatus = "success";
      })
      .addCase(createOnboardingLink.fulfilled, (state) => {
        state.actionStatus = "success";
      })
      .addCase(createLoginLink.fulfilled, (state) => {
        state.actionStatus = "success";
      });
  },
});

export const { clearActionStatus, clearCantRemove } =
  paymentConnectionSlice.actions;
export default paymentConnectionSlice.reducer;
